import { useState, useEffect } from 'react';
import { UserPlus, User, X, Clock, IndianRupee, Trash2, Edit, CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import API_BASE_URL from '../../config';

const AddPlanView = ({ onClose }) => {
  const userInfo = JSON.parse(localStorage.getItem('fitscan_user'));
  const gymCode = userInfo.gymcode;

  // Success message state
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);

  // Delete confirmation state - INSIDE main modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);

  const [plans, setPlans] = useState([]);
  const [formData, setFormData] = useState({
    planName: '',
    duration: '',
    GymCode: gymCode,
    amount: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);

  // Fetch all plans on mount
  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/plan/gym/${gymCode}`,
        {
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
          },
        }
      );
      if (!res.ok) throw new Error('Failed to fetch plans');
      const data = await res.json();
      setPlans(data);
    } catch (err) {
      console.error(err);
      showErrorMsg('Error fetching plans');
    }
  };

  useEffect(() => {
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Validation function
  const validateForm = () => {
    const newErrors = {};

    if (!formData.planName?.trim()) {
      newErrors.planName = 'Plan name is required';
    } else if (formData.planName.length < 2) {
      newErrors.planName = 'Plan name must be at least 2 characters';
    }

    const durationNum = parseInt(formData.duration);
    if (!formData.duration || isNaN(durationNum) || durationNum <= 0) {
      newErrors.duration = 'Duration must be atleast 1 day';
    }

    const amountNum = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amountNum) || amountNum <= 0) {
      newErrors.amount = 'Amount must be a positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Show success message
  const showSuccessMsg = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 3000);
  };

  // Show error message
  const showErrorMsg = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 5000);
  };

  // Delete confirmation handlers
  const handleDeleteClick = (plan) => {
    setPlanToDelete(plan);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete?.id) return;
    
    setShowDeleteConfirm(false); // Hide confirmation first
    
    try {
      const res = await fetch(`${API_BASE_URL}/api/plan/${planToDelete.id}`, { 
        method: 'DELETE',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}` 
        },
      });
      if (!res.ok) throw new Error('Failed to delete plan');
      await fetchPlans();
      showSuccessMsg('Plan deleted successfully!');
    } catch (err) {
      console.error(err);
      showErrorMsg('Error deleting plan');
    } finally {
      setPlanToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setPlanToDelete(null);
  };

  // Save or update plan
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showErrorMsg('Please fix the errors');
      return;
    }

    setLoading(true);

    try {
      const method = editingPlanId ? 'PUT' : 'POST';
      const url = editingPlanId
        ? `${API_BASE_URL}/api/plan/${editingPlanId}`
        : `${API_BASE_URL}/api/plan/Addplan`;

      const payload = {
        planName: formData.planName.trim(),
        duration: formData.duration,
        gymCode: formData.GymCode ?? formData.gymCode,
        amount: String(parseFloat(formData.amount)),
      };

      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}` 
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error('Failed to save plan');
      
      await fetchPlans();
      const action = editingPlanId ? 'updated' : 'added';
      showSuccessMsg(`Plan ${action} successfully!`);
      resetForm();
    } catch (err) {
      console.error(err);
      showErrorMsg('Error saving plan');
    } finally {
      setLoading(false);
    }
  };

  // Edit plan
  const handleEdit = (plan) => {
    setEditingPlanId(plan.id);
    setFormData({
      planName: plan.planName,
      duration: plan.duration,
      GymCode: plan.gymCode,
      amount: plan.amount,
    });
    setErrors({});
    setShowDeleteConfirm(false); // Close delete modal if open
  };

  // Reset form
  const resetForm = () => {
    setFormData({ planName: '', duration: '', GymCode: gymCode, amount: '' });
    setEditingPlanId(null);
    setErrors({});
    setShowDeleteConfirm(false); // Close delete modal if open
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative">
        {/* Delete Confirmation Overlay - INSIDE main modal */}
        {showDeleteConfirm && planToDelete && (
          <>
            <div className="fixed inset-0 bg-black/50 z-10 absolute" />
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 bg-[#181818] border border-[#2b2b2b] rounded-xl w-full max-w-md max-h-[80vh] shadow-2xl">
              <div className="p-6">
                <div className="flex items-start gap-3 mb-6">
                  <div className="p-2 bg-red-500/20 rounded-lg flex-shrink-0 mt-0.5">
                    <AlertTriangle size={28} className="text-red-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-white mb-1">Delete Plan?</h3>
                    <p className="text-gray-300 text-sm leading-relaxed">
                      Are you sure you want to delete <strong className="break-words">{planToDelete.planName}</strong>?
                    </p>
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    onClick={handleCancelDelete}
                    className="px-6 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg hover:bg-[#2b2b2b] transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-all text-sm"
                  >
                    Delete Plan
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <div className={`transition-all duration-200 ${showDeleteConfirm ? 'pointer-events-none opacity-50' : ''}`}>
          <div className="flex justify-between items-center p-6 border-b border-[#2b2b2b]">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <UserPlus className="text-lime-400" size={20} />
              Manage Plans
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-[#2b2b2b] rounded">
              <X size={20} />
            </button>
          </div>

          {/* Success/Error Message */}
          {showSuccess && !showDeleteConfirm && (
            <div className={`p-4 mx-6 mt-4 rounded-lg flex items-center gap-3 ${
              successMessage.includes('Error') || successMessage.includes('fix')
                ? 'bg-red-500/20 border border-red-500/50 text-red-300' 
                : 'bg-lime-500/20 border border-lime-500/50 text-lime-300'
            }`}>
              {successMessage.includes('Error') || successMessage.includes('fix') ? (
                <AlertCircle size={20} />
              ) : (
                <CheckCircle size={20} />
              )}
              <span>{successMessage}</span>
            </div>
          )}

          <div className="p-6 space-y-6">
            {/* Existing Plans List */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Existing Plans</h3>
              {plans.length === 0 ? (
                <p className="text-gray-400">No plans added yet.</p>
              ) : (
                <div className="w-full border border-[#2b2b2b] rounded-lg overflow-hidden">
                  <table className="w-full text-left">
                    <thead className="bg-[#222] text-gray-300">
                      <tr>
                        <th className="px-3 py-2 w-2/5 min-w-[130px]">Name</th>
                        <th className="px-3 py-2 w-1/5 min-w-[40px] text-center">Days</th>
                        <th className="px-3 py-2 w-1/5 min-w-[40px] text-center">Amount</th>
                        <th className="px-2 py-2 w-1/5 min-w-[60px]">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan) => (
                        <tr key={plan.id} className="border-t border-[#2b2b2b] hover:bg-[#222]">
                          <td className="px-3 py-3 break-words max-w-[130px] hyphens-auto">
                            {plan.planName}
                          </td>
                          <td className="px-3 py-3 text-center">{plan.duration}</td>
                          <td className="px-3 py-3 text-center">₹{plan.amount}</td>
                          <td className="px-2 py-2">
                            <div className="flex gap-1 justify-end">
                              <button
                                onClick={() => handleEdit(plan)}
                                title="Edit"
                                className="p-1.5 hover:bg-lime-400/20 rounded flex-shrink-0 w-7 h-7 flex items-center justify-center"
                              >
                                <Edit size={16} className="text-lime-400" />
                              </button>
                              <button
                                onClick={() => handleDeleteClick(plan)}
                                title="Delete"
                                className="p-1.5 hover:bg-red-400/20 rounded flex-shrink-0 w-7 h-7 flex items-center justify-center"
                              >
                                <Trash2 size={16} className="text-red-400" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Form - Only show when not in delete confirmation */}
            {!showDeleteConfirm && (
              <form onSubmit={handleFormSubmit} className="space-y-4">
                <h3 className="text-lg font-semibold">{editingPlanId ? 'Edit Plan' : 'Add New Plan'}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Plan Name */}
                  <div className="space-y-1">
                    <label className="text-sm text-gray-400">Plan Name <span className="text-red-400">*</span></label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="text"
                        name="planName"
                        value={formData.planName}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                          errors.planName
                            ? 'border-red-500 focus:ring-red-400'
                            : 'border-[#2b2b2b] focus:ring-lime-400'
                        }`}
                      />
                    </div>
                    {errors.planName && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle size={14} /> {errors.planName}
                      </p>
                    )}
                  </div>

                  {/* Duration */}
                  <div className="space-y-1">
                    <label className="text-sm text-gray-400">No of Days <span className="text-red-400">*</span></label>
                    <div className="relative">
                      <Clock size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="number"
                        min="1"
                        step="1"
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                          errors.duration
                            ? 'border-red-500 focus:ring-red-400'
                            : 'border-[#2b2b2b] focus:ring-lime-400'
                        }`}
                      />
                    </div>
                    {errors.duration && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle size={14} /> {errors.duration}
                      </p>
                    )}
                  </div>

                  {/* Amount */}
                  <div className="space-y-1 md:col-span-2">
                    <label className="text-sm text-gray-400">Amount (₹) <span className="text-red-400">*</span></label>
                    <div className="relative">
                      <IndianRupee size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        name="amount"
                        value={formData.amount}
                        onChange={handleChange}
                        className={`w-full pl-10 pr-4 py-2 bg-[#222] border-2 rounded-lg focus:outline-none focus:ring-2 transition-all ${
                          errors.amount
                            ? 'border-red-500 focus:ring-red-400'
                            : 'border-[#2b2b2b] focus:ring-lime-400'
                        }`}
                      />
                    </div>
                    {errors.amount && (
                      <p className="text-xs text-red-400 mt-1 flex items-center gap-1">
                        <AlertCircle size={14} /> {errors.amount}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-6">
                  {editingPlanId && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-2 bg-[#222] border border-[#2b2b2b] rounded-lg hover:bg-[#2b2b2b] transition-colors"
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-lime-400 text-[#181f10] rounded-lg hover:bg-lime-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {loading ? 'Saving...' : editingPlanId ? 'Update Plan' : 'Add Plan'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddPlanView;
